/**
 * Register Page for Re:Nav-96
 *
 * Allows users to create a new account with email, password, and display name.
 * Redirects to dashboard on successful registration.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons';

function sanitizeNext(next: string | null): string | null {
  if (!next) return null;
  if (!next.startsWith('/') || next.startsWith('//')) return null;
  return next;
}

/**
 * Password validation rules
 */
const PASSWORD_MIN_LENGTH = 8;

function validatePassword(password: string): string[] {
  const errors: string[] = [];

  if (password.length < PASSWORD_MIN_LENGTH) {
    errors.push(`At least ${PASSWORD_MIN_LENGTH} characters`);
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('One uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('One lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('One number');
  }

  return errors;
}

/**
 * Register page component
 * Provides registration form for new users
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, error, isLoading, clearError } = useAuth();

  const nextParam = sanitizeNext(searchParams.get('next'));
  const loginHref = nextParam ? `/login?next=${encodeURIComponent(nextParam)}` : '/login';

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');

  // Validation state
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * Handle password change and validation
   */
  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value) {
      setPasswordErrors(validatePassword(value));
    } else {
      setPasswordErrors([]);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    setFormError(null);

    // Validate password requirements
    const pwdErrors = validatePassword(password);
    if (pwdErrors.length > 0) {
      setPasswordErrors(pwdErrors);
      setFormError('Please fix the password requirements');
      return;
    }

    // Check password confirmation
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    // Validate display name
    if (displayName.trim().length < 2) {
      setFormError('Display name must be at least 2 characters');
      return;
    }

    try {
      await register({
        email,
        password,
        displayName: displayName.trim(),
        phone: phone.trim() || undefined,
      });
      // Navigate to the intended destination (e.g. /i/:code) or default dashboard.
      navigate(nextParam || '/member/dashboard', { replace: true });
    } catch {
      // Error is handled by AuthContext and displayed via error state
    }
  };

  const displayError = formError || error;
  const isPasswordValid = password.length > 0 && passwordErrors.length === 0;

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold mb-2">
            <span className="text-gradient">Join Re:Nav-96</span>
          </h1>
          <p className="text-white/60">
            Create your account to connect with your batchmates
          </p>
        </div>

        {/* Register Card */}
        <div className="card">
          {/* Social Login Buttons */}
          <SocialLoginButtons disabled={isLoading} />

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-secondary-light text-white/40">
                or create with email
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {displayError && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm">{displayError}</p>
              </div>
            )}

            {/* Display Name Field */}
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-white/80 mb-2">
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="input"
                placeholder="Your name as shown to others"
                required
                autoComplete="name"
                disabled={isLoading}
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="your.email@example.com"
                required
                autoComplete="email"
                disabled={isLoading}
              />
            </div>

            {/* Phone Field (Optional) */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
                Phone Number
                <span className="text-white/40 font-normal ml-1">(optional)</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input"
                placeholder="+91 98765 43210"
                autoComplete="tel"
                disabled={isLoading}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className="input pr-12"
                  placeholder="Create a secure password"
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password Requirements */}
              {password && (
                <div className="mt-2 text-xs">
                  <p className="text-white/40 mb-1">Password requirements:</p>
                  <ul className="space-y-0.5">
                    <li className={passwordErrors.includes(`At least ${PASSWORD_MIN_LENGTH} characters`) ? 'text-red-400' : 'text-green-400'}>
                      {passwordErrors.includes(`At least ${PASSWORD_MIN_LENGTH} characters`) ? '✗' : '✓'} At least {PASSWORD_MIN_LENGTH} characters
                    </li>
                    <li className={passwordErrors.includes('One uppercase letter') ? 'text-red-400' : 'text-green-400'}>
                      {passwordErrors.includes('One uppercase letter') ? '✗' : '✓'} One uppercase letter
                    </li>
                    <li className={passwordErrors.includes('One lowercase letter') ? 'text-red-400' : 'text-green-400'}>
                      {passwordErrors.includes('One lowercase letter') ? '✗' : '✓'} One lowercase letter
                    </li>
                    <li className={passwordErrors.includes('One number') ? 'text-red-400' : 'text-green-400'}>
                      {passwordErrors.includes('One number') ? '✗' : '✓'} One number
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
                Confirm Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
                disabled={isLoading}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
              )}
              {confirmPassword && password === confirmPassword && isPasswordValid && (
                <p className="text-green-400 text-xs mt-1">Passwords match</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !isPasswordValid || password !== confirmPassword}
              className="btn-primary w-full mt-6"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-secondary-light text-white/40">
                Already have an account?
              </span>
            </div>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <Link to={loginHref} className="btn-secondary w-full">
              Sign In Instead
            </Link>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/" className="link text-sm">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
